import {
  afterNextRender,
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ILexOfficeInvoiceDto, IProductDTO } from '../../models';
import { CommonService } from './common.service';
import { MetaTrackingService } from './meta-tracking.service';
import { RequestService } from './request.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class ShopService {
  products = signal([] as IProductDTO[]);
  visibleProducts = computed(() =>
    this.products().filter((product) => {
      const isNotRentable =
        !this.common.isRentable() && product.tags?.includes('Mieten');
      return !isNotRentable && product.visible;
    })
  );
  cart = signal([] as IProductDTO[]);

  shippingCountry = signal('DE');
  shippingMethod = signal<'shipping' | 'collection'>('shipping');

  atEffect = effect(
    () => {
      if (this.shippingCountry() === 'AT') {
        this.isTaxless.set(true);
      }
    },
    { allowSignalWrites: true }
  );
  /* shippingCountry = computed(() => {
    const countryCode = this.customerData()?.addresses?.shipping?.countryCode;
    if (!countryCode) {
      return 'DE';
    }
    return countryCode;
  }); */

  shippingCost = computed(() => {
    if (this.shippingMethod() === 'collection') {
      return { netPrice: 0, grossPrice: 0, taxRate: 19 };
    }
    let baseCost = 0;
    let taxRate = 19;
    let hasRepairProduct = false;

    const countryCode = this.shippingCountry();
    switch (countryCode) {
      case 'DE':
        baseCost = 379;
        taxRate = 19;
        break;
      case 'AT':
        baseCost = 479;
        taxRate = 0;
        break;
    }


    const base = this.cart().reduce((total, product) => {
      const isRepairProduct = environment.repairShop.articles.includes(product.id)
      if (isRepairProduct) {
        let shipping = 13.49
        if (product.id === 'ad9b9cf8-836d-40be-a8d5-5496db859817') {
          shipping += 26.51;
          return total + shipping;
        }
        if (hasRepairProduct) {
          return total;
        }
        hasRepairProduct = true;
        return total + shipping;
      }
      return total + (product.shipping ? baseCost : 0);
    }, 0);

    return {
      netPrice: base,
      //Apply Tax and round for 2 digits
      grossPrice:
        Math.round((base * (1 + taxRate / 100) + Number.EPSILON) * 100) / 100,
      taxRate,
    };
  });
  isCheckout = signal(false);
  orderSubmission: ILexOfficeInvoiceDto | null = null;
  customerData = computed(() => {
    const data = this.user.data();
    if (data && data.contact) {
      return data.contact;
    }
  });
  isTaxless = signal(true);
  onCartUpdate;
  injector = inject(Injector);
  private readonly tracking = inject(MetaTrackingService);
  customerDataEffect = effect(
    () => {
      const countryCode = this.customerData()?.addresses?.shipping?.countryCode;
      if (!countryCode) {
        this.shippingCountry.set('DE');
        return;
      }
      this.shippingCountry.set(countryCode);
    },
    { allowSignalWrites: true }
  );

  constructor(
    private request: RequestService,
    private user: UserService,
    private common: CommonService
  ) {
    afterNextRender(() => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        this.cart.set(JSON.parse(savedCart));
      }

      this.onCartUpdate = effect(
        () => {
          if (localStorage) {
            localStorage.setItem('cart', JSON.stringify(this.cart()));
          }
        },
        { injector: this.injector }
      );
    });
  }

  async fetchProducts() {
    const articleIds = environment.productsToFetch.map(p => p.id);
    const results = await lastValueFrom(this.request.getArticlesByIds(articleIds));

    const products = results.map(data => {
      const productRequest = environment.productsToFetch.find(p => p.id === data.id);
      return {
        ...data,
        ...productRequest,
      } as IProductDTO;
    });

    this.products.set(products);
    return products;
  }

  addToCart(product: IProductDTO) {
    this.cart.update((cart) => {
      const updatedCart = [...cart, product];
      if (product.childArticles) {
        product.childArticles.forEach((childArticle) => {
          const childProduct = this.products().find(
            (p) => p.id === childArticle.id
          );
          if (childProduct) {
            updatedCart.push({ ...childProduct, parentId: product.id });
          }
        });
      }
      return updatedCart;
    });
    // Meta-Pixel: Warenkorb-Ereignis für das Hauptprodukt (Child-Artikel zählen
    // wir nicht separat, um AddToCart nicht künstlich zu vervielfachen).
    this.tracking.addToCart(product);
  }

  removeFromCart(index: number) {
    const product = this.cart()[index];
    this.cart.update((cart) => {
      let updatedCart = [...cart];
      updatedCart.splice(index, 1);
      if (product.childArticles) {
        for (let childArticle of product.childArticles) {
          let childToRemove = updatedCart.find(
            (item) => item.id === childArticle.id
          );
          if (childToRemove) {
            updatedCart.splice(updatedCart.indexOf(childToRemove), 1);
          }
        }
      } else if (product.parentId) {
        updatedCart = updatedCart.filter(
          (item) => item.parentId !== product.parentId
        );
      }
      return updatedCart;
    });
  }

  groupedCart = computed(() => {
    const groupedCart = [];
    const parentProducts = this.cart().filter((product) => !product.parentId);
    parentProducts.forEach((parentProduct) => {
      let children = [];
      if (parentProduct.childArticles) {
        for (let childArticle of parentProduct.childArticles) {
          const child = this.cart().find(
            (product) => product.id === childArticle.id
          );
          children.push(child);
        }
      }
      groupedCart.push({ parent: parentProduct, children });
    });
    return groupedCart;
  });

  recurringTotal = computed(() =>
    this.cart()
      .filter((product) => product.tags?.includes('Mieten'))
      .reduce(
        (total, product) =>
          total +
          (this.isTaxless()
            ? product.price.netPrice
            : product.price.grossPrice),
        0
      )
  );

  upfrontTotal = computed(() => {
    const isTaxless = this.isTaxless();
    const shippingCost = this.shippingCost();

    return this.cart()
      .filter((product) => !product.tags?.includes('Mieten'))
      .reduce(
        (total, product) =>
          total +
          (isTaxless ? product.price.netPrice : product.price.grossPrice),
        isTaxless ? shippingCost.netPrice : shippingCost.grossPrice
      );
  });

  total = computed(() => {
    // upfrontTotal() enthält die Versandkosten bereits (pro Automat einmal,
    // siehe shippingCost). Hier NICHT erneut addieren – sonst doppelter Versand.
    return this.recurringTotal() + this.upfrontTotal();
  });

  buildInvoiceDto(): ILexOfficeInvoiceDto {
    const cart = this.cart();

    let remark = 'Lagerware - Ihre Ware wird nach Erhalt der Zahlung versandt.';
    if (cart.find((product: IProductDTO) => product.tags?.includes('Kaufen'))) {
      remark += ' Zum Versand wird die Terminal-ID benötigt.';
    }
    if (this.shippingMethod() === 'collection') {
      remark += ' VERSAND: Selbstabholung.';
    }

    const { lexContact } = this.user.data() || {};
    const shippingCost = this.shippingCost();
    const orderSubmission: ILexOfficeInvoiceDto = {
      archived: false,
      voucherDate: new Date().toISOString(),
      address: {
        contactId: lexContact?.id,
      },
      lineItems: [
        ...this.cart().map((product) => ({
          id: product.id,
          name: product.title,
          description: product.description,
          quantity: 1,
          unitName: 'Stück',
          unitPrice: {
            currency: 'EUR',
            netAmount: product.price.netPrice,
            taxRatePercentage: shippingCost.taxRate,
          },
          type: 'material',
        })),
        {
          name: 'Versand',
          quantity: 1,
          unitName: 'Stück',
          unitPrice: {
            currency: 'EUR',
            netAmount: shippingCost.netPrice,
            taxRatePercentage: shippingCost.taxRate,
          },
          type: 'custom',
        },
      ],
      totalPrice: {
        currency: 'EUR',
      },
      taxConditions: {
        taxType: 'net',
      },
      shippingConditions: {
        shippingType: 'none',
      },
      paymentConditions: {
        paymentTermLabel:
          'Der Betrag wird in den nächsten 7 Tagen automatisch von ihrem Konto eingezogen.',
        paymentTermLabelTemplate:
          'Der Betrag wird in den nächsten 7 Tagen automatisch von ihrem Konto eingezogen.',
        paymentTermDuration: 7,
      },
      remark,
    };

    return orderSubmission;
  }

  async submitOrder() {
    try {
      const orderSubmission = this.buildInvoiceDto();
      const isRequesting = this.common.isRequesting();
      if (isRequesting) return;
      this.common.isRequesting.set(true);
      // Gemeinsame ID für Browser-Pixel + Server-CAPI (Meta-Deduplizierung).
      const eventId = this.tracking.newEventId();
      const customData = {
        value: Math.round(this.total() * 100) / 100,
        currency: 'EUR',
      };
      this.tracking.track('Purchase', customData, eventId);
      const contactPerson = this.user.data()?.lexContact?.company?.contactPersons?.[0];
      const response = await lastValueFrom(
        await this.request.postInvoice(orderSubmission, eventId, {
          eventSourceUrl: this.tracking.currentPageUrl(),
          eventCustomData: customData,
          eventUserData: {
            email: contactPerson?.emailAddress,
            phone: contactPerson?.phoneNumber,
            firstName: contactPerson?.firstName,
            lastName: contactPerson?.lastName,
          },
        })
      );
      if (response && response.id) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error placing order:', error);
      return false;
    } finally {
      this.common.isRequesting.set(false);
    }
  }

  emptyCart() {
    this.cart.set([]);
  }
}
